import moment from 'moment'
import { Link } from 'react-router-dom'
import {
  Button,
  ButtonColor,
  ButtonStyle,
  Icon,
  IconType,
  Slider,
} from '../../../../components'
import { Divider } from '../../../../components/Divider'
import { Avatar } from '../../../../elements'
import { Collaboration } from '../../../../gql/types'
import { EDIT_REQUEST_DEADLINE_DAYS } from '../../../../utils/constants'
import { replaceURLs } from '../../../../utils/helpers'
import { CreatorRoutes } from '../../../CreatorRoutes'

interface IProps {
  open: boolean
  setOpen: (open: boolean) => void
  collaboration: Collaboration
}

export const EditRequestSlider = ({ open, setOpen, collaboration }: IProps) => {
  const { campaign, creator, uuid } = collaboration
  const editsRequestedAt = collaboration.metadata?.editsRequestedAt

  return (
    <Slider setOpen={setOpen} open={open} size='md'>
      <div className='flex h-full flex-col overflow-y-auto bg-white shadow-xl w-full'>
        {/* Header */}
        <div className='sticky top-0 z-10 bg-white'>
          <div className='px-4 pt-4 sm:px-6'>
            <div className='flex items-start justify-between'>
              <Avatar
                type='product'
                src={campaign.product.photo?.url}
                size='md'
                alt={campaign.product.name}
              />
              <div className='ml-3 flex h-7 items-center'>
                <button
                  type='button'
                  className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none'
                  onClick={() => setOpen(false)}
                >
                  <span className='sr-only'>Close panel</span>
                  <Icon
                    type={IconType.x}
                    className='h-6 w-6'
                    aria-hidden='true'
                  />
                </button>
              </div>
            </div>
            <h3 className='text-xl font-bold text-gray-900 mt-4'>
              Edit Request
            </h3>
            <Link
              to={CreatorRoutes.collaborationViewBriefDetails
                .replace(':id', `${creator.id}`)
                .replace(':uuid', uuid)}
              className='text-sm text-blue-600 hover:text-blue-500'
            >
              {campaign.product.name}
            </Link>
            {collaboration.canUploadClip && (
              <div className='mt-2 flex flex-wrap font-bold'>
                <Button
                  title='Upload edited clip'
                  type='button'
                  styleType={ButtonStyle.fill}
                  colour={ButtonColor.primary}
                  className='flex-1 font-bold'
                  to={CreatorRoutes.collaborationViewClips
                    .replace(':id', `${creator.id}`)
                    .replace(':uuid', uuid)}
                />
              </div>
            )}
          </div>
          <Divider className='py-6' />
        </div>

        {/* Message card - same pattern as ChatMessage */}
        <div className='pb-1 sm:pb-6 px-4 sm:px-6'>
          <div className='bg-white px-4 py-6 shadow sm:rounded-lg sm:px-6'>
            <div className='sm:flex sm:items-baseline sm:justify-between'>
              <h3 className='text-base w-full font-medium flex flex-row items-center'>
                <div className='h-9 w-9 flex-shrink-0 relative'>
                  <Avatar
                    className='h-9 w-9'
                    size='xs'
                    src={campaign.brand?.logo?.url}
                    type='brand'
                    alt='Logo'
                  />
                </div>
                <div className='w-full flex flex-row items-center justify-between'>
                  <div className='text-gray-900 pl-3 text-sm truncate max-w-[160px]'>
                    {campaign.brand?.name}
                  </div>
                  {editsRequestedAt && (
                    <p className='mt-1 whitespace-nowrap text-sm text-gray-600 sm:mt-0 sm:ml-3'>
                      <time dateTime={editsRequestedAt}>
                        {moment(editsRequestedAt).format('ll')}
                      </time>
                    </p>
                  )}
                </div>
              </h3>
            </div>
            {collaboration.editsRequestMessage && (
              <div
                className='mt-4 space-y-6 text-sm text-gray-800'
                dangerouslySetInnerHTML={{
                  __html: replaceURLs(
                    collaboration.editsRequestMessage,
                  )?.replace(/\n/g, '<div></div>'),
                }}
              />
            )}
          </div>

          <div className='mt-4 border-l-2 border-gray-300 pl-3'>
            <p className='text-xs italic text-gray-500'>
              ❗ You have {EDIT_REQUEST_DEADLINE_DAYS} days to complete the
              requested edits.
            </p>
          </div>
        </div>
      </div>
    </Slider>
  )
}
